import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { tokens } from '../../theme';
import TableCell from './TableCell';
import { defaultTableConfig, valuesMatch } from './tableUtils';

/**
 * TableReadOnly Component
 * Displays submitted table answers in read-only mode.
 * Shows correctness indicators for each cell (for teachers/admins).
 */
const TableReadOnly = ({
  config = defaultTableConfig,
  studentAnswer = { rows: [] },
  correctAnswer = null,
  isCorrect = null,
  showCorrect = false,  // Whether to show correct answers (for teachers)
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const safeStudentAnswer = studentAnswer || { rows: [] };

  const getColumnType = (columnId) => {
    return config.columns?.find(c => c.id === columnId)?.type || 'math';
  };

  const isCellPrefilled = (rowId, colId) => {
    const configRow = config.rows?.find(r => r.id === rowId);
    return configRow?.cells?.[colId]?.prefilled || false;
  };

  const getPrefilledValue = (rowId, colId) => {
    const configRow = config.rows?.find(r => r.id === rowId);
    return configRow?.cells?.[colId]?.value || '';
  };

  const getStudentValue = (rowId, colId) => {
    const row = safeStudentAnswer.rows?.find(r => r.id === rowId);
    return row?.cells?.[colId] || '';
  };

  const getCorrectValue = (rowId, colId) => {
    if (!correctAnswer) return null;
    const row = correctAnswer.rows?.find(r => r.id === rowId);
    return row?.cells?.[colId] || '';
  };

  const checkCellCorrectness = (rowId, colId) => {
    if (!correctAnswer || !showCorrect) return null;
    if (isCellPrefilled(rowId, colId)) return null; // Don't check prefilled cells

    const studentValue = getStudentValue(rowId, colId);
    const correctValue = getCorrectValue(rowId, colId);
    const colType = getColumnType(colId);
    const tolerance = config.grading?.tolerance || 0.01;
    const caseSensitive = config.grading?.caseSensitive || false;

    // For tolerance mode, use tolerance; for exact, use 0
    const effectiveTolerance = config.grading?.mode === 'tolerance' ? tolerance : 0;

    return valuesMatch(studentValue, correctValue, colType, effectiveTolerance, caseSensitive);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Overall correctness indicator */}
      {isCorrect !== null && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCorrect ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Correct"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              icon={<CancelIcon />}
              label="Incorrect"
              color="error"
              size="small"
            />
          )}
        </Box>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {config.columns?.map((col) => (
                <MuiTableCell
                  key={col.id}
                  sx={{
                    fontWeight: 'bold',
                    minWidth: 100,
                    backgroundColor: colors.primary[500],
                    color: '#fff',
                  }}
                >
                  {col.type === 'math' ? (
                    <TableCell
                      value={col.header}
                      type="math"
                      readOnly
                      size="small"
                    />
                  ) : (
                    col.header || '-'
                  )}
                </MuiTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {config.rows?.map((configRow) => (
              <TableRow key={configRow.id}>
                {config.columns?.map((col) => {
                  const prefilled = isCellPrefilled(configRow.id, col.id);
                  const studentValue = prefilled
                    ? getPrefilledValue(configRow.id, col.id)
                    : getStudentValue(configRow.id, col.id);
                  const correctValue = getCorrectValue(configRow.id, col.id);
                  const cellCorrect = checkCellCorrectness(configRow.id, col.id);

                  return (
                    <MuiTableCell
                      key={col.id}
                      sx={{
                        backgroundColor: prefilled
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.03)'
                          : cellCorrect === true
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(76, 175, 80, 0.15)'
                              : 'rgba(76, 175, 80, 0.1)'
                            : cellCorrect === false
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(244, 67, 54, 0.15)'
                                : 'rgba(244, 67, 54, 0.1)'
                              : 'transparent',
                        padding: '8px',
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableCell
                          value={studentValue}
                          type={getColumnType(col.id)}
                          readOnly
                          size="small"
                          sx={{ flexGrow: 1 }}
                        />
                        {!prefilled && cellCorrect !== null && (
                          cellCorrect ? (
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{ color: 'success.main', flexShrink: 0 }}
                            />
                          ) : (
                            <CancelIcon
                              fontSize="small"
                              sx={{ color: 'error.main', flexShrink: 0 }}
                            />
                          )
                        )}
                      </Box>

                      {/* Show correct answer if incorrect and showCorrect is true */}
                      {showCorrect && !prefilled && cellCorrect === false && correctValue && (
                        <Box
                          sx={{
                            mt: 0.5,
                            pt: 0.5,
                            borderTop: '1px dashed',
                            borderColor: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: 'success.main', fontSize: '0.65rem' }}
                          >
                            Correct:
                          </Typography>
                          <TableCell
                            value={correctValue}
                            type={getColumnType(col.id)}
                            readOnly
                            size="small"
                            sx={{ '& math-field': { color: 'success.main' } }}
                          />
                        </Box>
                      )}
                    </MuiTableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      {showCorrect && (
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Correct</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Incorrect</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Prefilled</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TableReadOnly;
